(() => {
  if (window.__selecturnsLinkInBio && window.__selecturnsLinkInBio.loaded) {
    window.__selecturnsLinkInBio.mountAll?.();
    return;
  }

  const instances = new WeakMap();
  const reduceMotionQuery = window.matchMedia ? window.matchMedia('(prefers-reduced-motion: reduce)') : null;
  const finePointerQuery = window.matchMedia ? window.matchMedia('(hover: hover) and (pointer: fine) and (min-width: 750px)') : null;

  function prefersReducedMotion() {
    return Boolean(reduceMotionQuery && reduceMotionQuery.matches);
  }

  function canUseTilt() {
    return Boolean(finePointerQuery && finePointerQuery.matches && !prefersReducedMotion());
  }

  function revealAll(items) {
    items.forEach((item) => item.classList.add('is-revealed'));
  }

  function copyFallback(value) {
    const textarea = document.createElement('textarea');
    textarea.value = value;
    textarea.setAttribute('readonly', '');
    textarea.style.position = 'fixed';
    textarea.style.inset = '0 auto auto 0';
    textarea.style.opacity = '0';
    document.body.appendChild(textarea);
    textarea.select();
    let copied = false;
    try {
      copied = document.execCommand('copy');
    } catch (error) {
      copied = false;
    }
    textarea.remove();
    return copied;
  }

  async function writeClipboard(value) {
    if (!value) return false;
    if (navigator.clipboard && navigator.clipboard.writeText) {
      try {
        await navigator.clipboard.writeText(value);
        return true;
      } catch (error) {
        return copyFallback(value);
      }
    }
    return copyFallback(value);
  }

  function mount(root) {
    if (!root || instances.has(root)) return;

    const revealItems = Array.from(root.querySelectorAll('[data-lib-reveal]'));
    const copyButtons = Array.from(root.querySelectorAll('[data-lib-copy]'));
    const card = root.querySelector('[data-lib-card]');
    const animationsEnabled = root.dataset.libAnimations === 'true' && !prefersReducedMotion();
    const tiltEnabled = root.dataset.libTilt === 'true' && canUseTilt();

    root.classList.add('is-ready');

    let observer = null;
    if (animationsEnabled && 'IntersectionObserver' in window) {
      observer = new IntersectionObserver((entries) => {
        entries.forEach((entry) => {
          if (!entry.isIntersecting) return;
          entry.target.classList.add('is-revealed');
          observer.unobserve(entry.target);
        });
      }, { threshold: 0.12, rootMargin: '0px 0px -8% 0px' });

      revealItems.forEach((item) => observer.observe(item));
    } else {
      revealAll(revealItems);
    }

    const copyHandlers = [];
    copyButtons.forEach((button) => {
      const handler = async () => {
        const copied = await writeClipboard(button.dataset.copyValue || '');
        if (!copied) return;

        button.classList.add('is-copied');
        window.clearTimeout(button.__libCopyTimer);
        button.__libCopyTimer = window.setTimeout(() => {
          button.classList.remove('is-copied');
        }, 1150);
      };
      button.addEventListener('click', handler);
      copyHandlers.push([button, handler]);
    });

    let frame = 0;
    const tiltHandlers = [];
    if (tiltEnabled && card) {
      const onMove = (event) => {
        const rect = card.getBoundingClientRect();
        const x = (event.clientX - rect.left) / rect.width - 0.5;
        const y = (event.clientY - rect.top) / rect.height - 0.5;
        if (frame) window.cancelAnimationFrame(frame);
        frame = window.requestAnimationFrame(() => {
          card.style.transform = `perspective(900px) rotateX(${(-y * 4).toFixed(2)}deg) rotateY(${(x * 5).toFixed(2)}deg)`;
        });
      };

      const onLeave = () => {
        if (frame) window.cancelAnimationFrame(frame);
        card.style.transform = 'perspective(900px) rotateX(0deg) rotateY(0deg)';
      };

      card.addEventListener('mousemove', onMove);
      card.addEventListener('mouseleave', onLeave);
      tiltHandlers.push(['mousemove', onMove], ['mouseleave', onLeave]);
    }

    function onBlockSelect(event) {
      if (!window.Shopify || !window.Shopify.designMode) return;
      if (!event.detail || event.detail.sectionId !== root.dataset.sectionId) return;
      const block = root.querySelector(`[data-block-id="${event.detail.blockId}"]`);
      if (!block) return;
      block.classList.add('is-editor-selected');
      block.scrollIntoView({ block: 'center', behavior: prefersReducedMotion() ? 'auto' : 'smooth' });
    }

    function onBlockDeselect(event) {
      if (!window.Shopify || !window.Shopify.designMode) return;
      if (!event.detail || event.detail.sectionId !== root.dataset.sectionId) return;
      const block = root.querySelector(`[data-block-id="${event.detail.blockId}"]`);
      if (block) block.classList.remove('is-editor-selected');
    }

    if (window.Shopify && window.Shopify.designMode) {
      document.addEventListener('shopify:block:select', onBlockSelect);
      document.addEventListener('shopify:block:deselect', onBlockDeselect);
    }

    instances.set(root, () => {
      if (observer) observer.disconnect();
      copyHandlers.forEach(([button, handler]) => button.removeEventListener('click', handler));
      if (card) tiltHandlers.forEach(([name, handler]) => card.removeEventListener(name, handler));
      if (frame) window.cancelAnimationFrame(frame);
      if (window.Shopify && window.Shopify.designMode) {
        document.removeEventListener('shopify:block:select', onBlockSelect);
        document.removeEventListener('shopify:block:deselect', onBlockDeselect);
      }
      instances.delete(root);
    });
  }

  function unmount(root) {
    const destroy = instances.get(root);
    if (destroy) destroy();
  }

  function mountAll(scope = document) {
    scope.querySelectorAll('[data-link-in-bio]').forEach(mount);
  }

  window.__selecturnsLinkInBio = { loaded: true, mountAll, mount, unmount };

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', () => mountAll(), { once: true });
  } else {
    mountAll();
  }

  document.addEventListener('shopify:section:load', (event) => mountAll(event.target));
  document.addEventListener('shopify:section:unload', (event) => {
    const root = event.target.querySelector('[data-link-in-bio]');
    if (root) unmount(root);
  });
})();
